import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  AgreementEditDrawer,
  ClientLegalDrawer,
  ClientSelector,
  DateField,
  FileUploader,
  InvoiceNumberInput,
  InvoiceSelector,
  Label,
  PriceEdit,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '@hero/web-components';
import { FileTextIcon } from '@hero/web-icons';
import { useAgreementListApi, useClientApi } from '@hero/api-hooks';
import type { Client, Invoice } from '@hero/schema';
import {
  RequirementsCard,
  RequirementsCardSkeleton,
  clientLegalHardChecks,
  clientLegalSoftChecks,
  isAgreementComplete,
  isClientLegalHardComplete,
  isClientLegalSoftComplete,
  pickCurrentAgreement,
} from '../Requirements';
import { HeroInvoiceRequirementsList } from './HeroInvoiceRequirementsList';
import type { ApplyFormValues, InvoiceTabKey } from './types';

export type InvoiceFieldsetProps = {
  values: ApplyFormValues;
  onChange: <K extends keyof ApplyFormValues>(key: K, value: ApplyFormValues[K]) => void;
};

export function InvoiceFieldset({ values, onChange }: InvoiceFieldsetProps) {
  const { t } = useTranslation('m-financing');

  /**
   * Lazy-fetch the picked client so we can read its `client.legal`
   * sub-doc + look up the bilateral agreement. Guarded on
   * `externalClientId` so we don't run the query before the user
   * has picked anything.
   */
  const clientQuery = useClientApi(values.externalClientId || undefined);
  const pickedClient = clientQuery.data;

  /**
   * Agreement list filtered to the picked client; we then narrow
   * to "currently in force" (active / new) client-side because the
   * list API doesn't expose a status filter that maps cleanly to
   * what the financing gate considers valid.
   */
  // `partyId` matches `tenants.id` on the agreement; combined with
  // the server-side current-user scope this returns *only*
  // agreements between this carrier and the picked client.
  // Disabled until the user has picked someone — there's no
  // useful query before that.
  const agreementListQuery = useAgreementListApi(
    { partyId: values.externalClientId || undefined },
    { enabled: !!values.externalClientId },
  );
  // `useInfiniteQueryAPI` exposes `data` as an already-flat,
  // de-duplicated array (see `useInfinitePagination`). No
  // `.pages.flat()` needed — that's the raw TanStack shape.
  const agreementsForClient = useMemo(
    () => agreementListQuery.data ?? [],
    [agreementListQuery.data],
  );
  const currentAgreement = useMemo(
    () => pickCurrentAgreement(agreementsForClient),
    [agreementsForClient],
  );

  /**
   * Gate cards need both the client doc and the bilateral agreement
   * list before we can truthfully say what's missing. While either
   * query is in flight (or the client doc is still from a previous
   * selection) we show skeleton placeholders instead of flashing
   * red "required" badges that flip green a moment later.
   */
  const requirementsLoading =
    !!values.externalClientId &&
    (clientQuery.isLoading ||
      agreementListQuery.isLoading ||
      pickedClient?._id?.toString() !== values.externalClientId);

  const [isAgreementDrawerOpen, setIsAgreementDrawerOpen] = useState(false);
  const [isClientLegalDrawerOpen, setIsClientLegalDrawerOpen] = useState(false);

  // Derived check labels — surfaced inside the requirement cards
  // so the user sees the *exact* missing field without opening the
  // drawer.
  const clientHardChecks = clientLegalHardChecks(pickedClient);
  const clientSoftChecks = clientLegalSoftChecks(pickedClient);
  const clientHardMissing = [
    !clientHardChecks.registrationName &&
      t('apply_form.invoice.external.customer_legal.missing.registration_name'),
    !clientHardChecks.registrationId &&
      t('apply_form.invoice.external.customer_legal.missing.registration_id'),
    !clientHardChecks.taxId &&
      t('apply_form.invoice.external.customer_legal.missing.tax_id'),
  ].filter(Boolean) as string[];
  const clientSoftMissing = [
    !clientSoftChecks.registrationImage &&
      t('apply_form.invoice.external.customer_legal.missing.registration_image'),
    !clientSoftChecks.taxImage &&
      t('apply_form.invoice.external.customer_legal.missing.tax_image'),
  ].filter(Boolean) as string[];

  /**
   * When the carrier picks a Hero invoice, snapshot its id +
   * derived display amount into the form so the rest of the
   * wizard (review step, requested-amount sync, submit payload)
   * can read the live values without re-fetching.
   *
   * `amount.remaining` is preferred over `amount.total` because
   * for a partially-paid invoice only the remaining balance is
   * factorable.
   */
  const onHeroInvoicePick = (invoices: Invoice[] | undefined) => {
    const picked = invoices?.[0];
    onChange('heroInvoiceId', picked ? String(picked._id) : '');
    onChange(
      'heroInvoiceAmount',
      picked
        ? String(picked.amount?.remaining ?? picked.amount?.total ?? 0)
        : '',
    );
    // `buyer` replaced the legacy `payer` field in the SaaS-era
    // bilateral invoice rename. The selector trigger / row also
    // reads `buyer.name` for the same reason.
    onChange(
      'heroInvoiceCustomerName',
      picked?.buyer?.name ?? '',
    );
    // `invoice.ref` is structured `{ main, short, custom? }`; prefer
    // the short carrier-facing form (e.g. "HRO-20184") for the
    // wizard's invoice-number snapshot.
    onChange(
      'heroInvoiceNumber',
      picked?.ref?.short ?? picked?.ref?.main ?? '',
    );
    onChange(
      'heroExpectedDueDate',
      picked?.period?.due ? new Date(picked.period.due) : null,
    );
  };

  /**
   * The ClientSelector returns the full picked `Client[]`. We
   * normalise to a single entry (single-select mode below) and
   * snapshot both the id and the display name into the form so the
   * downstream submit payload doesn't need to re-fetch the client.
   */
  const onClientPick = (clients: Client[] | undefined) => {
    const picked = clients?.[0];
    onChange('externalClientId', picked?._id?.toString() ?? '');
    onChange('externalCustomerName', picked?.name ?? '');
  };

  /**
   * When the user creates / edits a Client inline (from the
   * selector's modal), the modal returns the saved doc. Mirror it
   * into the form so the user doesn't have to re-pick it.
   */
  const onClientSave = ({ client }: { client: Client }) => {
    onChange('externalClientId', client._id?.toString() ?? '');
    onChange('externalCustomerName', client.name);
  };

  return (
    <fieldset className="space-y-4">
      <legend className="mb-1">
        <h3 className="text-base font-semibold text-gray-900">
          {t('apply_form.invoice.title')}
        </h3>
        <p className="mt-1 text-xs text-gray-500">
          {t('apply_form.invoice.description')}
        </p>
      </legend>

      <Tabs
        value={values.invoiceTab}
        onValueChange={v => onChange('invoiceTab', v as InvoiceTabKey)}>
        <TabsList className="grid w-full grid-cols-2">
          <TabsTrigger value="hero">{t('apply_form.invoice.tabs.hero')}</TabsTrigger>
          <TabsTrigger value="external">
            {t('apply_form.invoice.tabs.external')}
          </TabsTrigger>
        </TabsList>

        {/*
         * `forceMount` on both tabs keeps the inactive panel rendered
         * (just visually hidden via `data-state="inactive"` styling).
         * Without it Radix unmounts the inactive tab, which would:
         *  - revoke the `blob:` URL of an uploaded invoice file
         *    (`useFilePreview` cleanup), so the thumbnail returns
         *    "Your file couldn't be accessed" after toggling back, and
         *  - reset internal Select / dropdown state for any selectors
         *    that mount inside the panel.
         * Hidden panels still receive `display: none` from Radix so
         * focus order is unaffected.
         */}
        <TabsContent value="hero" className="mt-4 space-y-4" forceMount>
          {/*
           * `financeableOnly` narrows the list to invoices that
           * still have a payable balance — paid / cancelled
           * invoices aren't useful for financing. The selector
           * itself drives infinite scroll + search and renders an
           * icon + customer + Hero ref + seller ref + due date +
           * price row per option so the user can scan their book.
           */}
          <InvoiceSelector
            label={t('apply_form.invoice.hero.select_label')}
            placeholder={t('apply_form.invoice.hero.select_placeholder')}
            selectedInvoices={values.heroInvoiceId || undefined}
            onInvoicesSelected={onHeroInvoicePick}
            query={{ financeableOnly: true }}
            required
          />

          {/*
           * Lender-packet gates for the picked Hero invoice. The
           * list fetches the invoice / client / agreement itself,
           * so the fieldset only carries the picked id around. All
           * three gates (invoice details / signed contract / client
           * legal) must be satisfied before the Continue button
           * unlocks — same predicate the wizard's submit gate uses.
           */}
          {values.heroInvoiceId ? (
            <HeroInvoiceRequirementsList invoiceId={values.heroInvoiceId} />
          ) : null}
        </TabsContent>

        <TabsContent value="external" className="mt-4 space-y-4" forceMount>
          <div>
            <Label className="mb-1.5 block">
              {t('apply_form.invoice.external.upload_label')}
            </Label>
            <FileUploader
              files={values.externalDocument ? [values.externalDocument] : []}
              onChange={files => onChange('externalDocument', files[0] ?? null)}
              label={t('apply_form.invoice.external.upload_hint')}
              emptyIcon={<FileTextIcon className="size-6 text-gray-400" />}
              variant="default"
              hint={t('apply_form.invoice.external.upload_hint')}
              status={values.externalDocument ? 'success' : 'neutral'}
            />
          </div>

          <div>
            <ClientSelector
              label={t('apply_form.invoice.external.client_name')}
              placeholder={t('apply_form.invoice.external.client_name_placeholder')}
              selectedClients={values.externalClientId || undefined}
              onClientsSelected={onClientPick}
              onClientSave={onClientSave}
              required
              selectMode="single"
            />
            <p className="mt-1.5 text-xs text-gray-500">
              {t('apply_form.invoice.external.client_name_hint')}
            </p>
          </div>

          {/*
           * Requirement gates — only shown once a client is picked.
           * Two cards, in this exact order, mirror the lender-packet
           * sequence:
           *
           *  1. Contract — bilateral agreement with `contractPDF`
           *     attached. Hard block: without a signed contract the
           *     lender has no recourse, so we don't even let the
           *     application leave the wizard.
           *  2. Client legal — CR number, Tax number, legal name.
           *     Hard. The CR/Tax document scans are *soft* (warning
           *     only) — the application can still go out without
           *     them, just at potentially worse terms.
           *
           * Cards are clickable; each opens its own drawer pre-
           * filled with the current state.
           */}
          {values.externalClientId ? (
            <div
              className="space-y-2"
              aria-busy={requirementsLoading}
              aria-live="polite">
              {requirementsLoading ? (
                <>
                  <RequirementsCardSkeleton />
                  <RequirementsCardSkeleton />
                </>
              ) : (
                <>
                  <RequirementsCard
                    title={t('apply_form.invoice.external.contract_card.title')}
                    description={t(
                      currentAgreement
                        ? 'apply_form.invoice.external.contract_card.description_with_agreement'
                        : 'apply_form.invoice.external.contract_card.description_empty',
                      { client: pickedClient?.name ?? '' },
                    )}
                    complete={isAgreementComplete(currentAgreement)}
                    level="hard"
                    actionLabel={t(
                      currentAgreement
                        ? 'apply_form.invoice.external.contract_card.action_existing'
                        : 'apply_form.invoice.external.contract_card.action_create',
                    )}
                    onAction={() => setIsAgreementDrawerOpen(true)}
                    missingItems={
                      isAgreementComplete(currentAgreement)
                        ? undefined
                        : currentAgreement
                          ? [t('apply_form.invoice.external.contract_card.missing_pdf')]
                          : [t('apply_form.invoice.external.contract_card.missing_agreement')]
                    }
                  />

                  <RequirementsCard
                    title={t('apply_form.invoice.external.customer_legal.title')}
                    description={t(
                      'apply_form.invoice.external.customer_legal.description',
                    )}
                    complete={
                      isClientLegalHardComplete(pickedClient) &&
                      isClientLegalSoftComplete(pickedClient)
                    }
                    /*
                     * Card-level severity is the WORST of the two
                     * sub-states: if the hard fields are missing, this
                     * is a blocking gate (red). If only the soft docs
                     * are missing, it degrades to a soft warning so the
                     * user understands they *can* still apply.
                     */
                    level={
                      !isClientLegalHardComplete(pickedClient) ? 'hard' : 'soft'
                    }
                    actionLabel={t(
                      'apply_form.invoice.external.customer_legal.action',
                    )}
                    onAction={() => setIsClientLegalDrawerOpen(true)}
                    missingItems={
                      isClientLegalHardComplete(pickedClient) &&
                      isClientLegalSoftComplete(pickedClient)
                        ? undefined
                        : [...clientHardMissing, ...clientSoftMissing]
                    }
                  />
                </>
              )}
            </div>
          ) : null}

          {/*
           * Two-column row: invoice amount + expected pay date.
           * Both columns use the same shell (manual `<Label>` +
           * control) so labels and inputs share a baseline. The due-
           * date helper sits *inside* the right column — never as a
           * third grid cell, which was pulling the date field out of
           * alignment with the amount field.
           */}
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 sm:items-start">
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="invoice-external-amount">
                {t('apply_form.invoice.external.amount')}
                <span className="text-error ms-0.5">*</span>
              </Label>
              <PriceEdit
                name="invoice-external-amount"
                value={values.externalAmount === '' ? undefined : Number(values.externalAmount)}
                onChange={v => onChange('externalAmount', v == null ? '' : String(v))}
                placeholder={t('apply_form.invoice.external.amount_placeholder')}
              />
            </div>

            <div className="flex flex-col gap-1.5">
              <Label htmlFor="invoice-external-due-date">
                {t('apply_form.invoice.external.expected_due_date')}
                <span className="text-error ms-0.5">*</span>
              </Label>
              <DateField
                id="invoice-external-due-date"
                value={values.externalExpectedDueDate}
                onChange={date => onChange('externalExpectedDueDate', date ?? null)}
              />
              <p className="text-xs text-gray-500">
                {t('apply_form.invoice.external.expected_due_date_hint')}
              </p>
            </div>
          </div>

          <div className="flex flex-col gap-1.5">
            <Label htmlFor="invoice-external-number">
              {t('apply_form.invoice.external.invoice_number')}
              <span className="text-error ms-0.5">*</span>
            </Label>
            {/*
             * Shared invoice-number input — owns the `#` glyph
             * adornment and the strip-on-paste rule. See
             * `InvoiceNumberInput` for the shape; the same
             * component is also reused by the carrier-dashboard
             * CRM "Create invoice from delivered orders" picker.
             */}
            <InvoiceNumberInput
              id="invoice-external-number"
              value={values.externalInvoiceNumber}
              onChange={next => onChange('externalInvoiceNumber', next)}
              placeholder={t(
                'apply_form.invoice.external.invoice_number_placeholder',
              )}
            />
          </div>
        </TabsContent>
      </Tabs>

      {/*
       * Requirement drawers / modal — mounted unconditionally so
       * their internal close animations can play. Visibility is
       * driven entirely by the `isOpen` props. Both rely on the
       * `useClientApi` / `useAgreementListApi` hooks above to
       * invalidate themselves on save (TanStack Query handles the
       * refetch via mutation `onSettled` in the api-hook layer), so
       * the gate cards re-evaluate as soon as the drawer closes.
       */}
      <AgreementEditDrawer
        isOpen={isAgreementDrawerOpen}
        onClose={() => setIsAgreementDrawerOpen(false)}
        agreementId={currentAgreement?._id}
        defaults={
          values.externalClientId
            ? {
                // Seed from the form values directly — `externalClientId`
                // / `externalCustomerName` are set the moment the user
                // picks a client in `<ClientSelector>`, so we don't
                // gate on the (slower) `useClientApi` fetch. Without
                // this, opening the contract drawer immediately after
                // picking a client mounts the agreement form with no
                // counterparty (defaults=undefined) and you'd have to
                // close+reopen for it to pick up.
                counterpartyId: values.externalClientId,
                counterpartyName:
                  pickedClient?.name ?? values.externalCustomerName,
                // `roles[]` only lands once the fetch resolves; the
                // manager falls back to `['business_customer']` when
                // we hand it `undefined`, so this is a no-risk enrich.
                counterpartyRoles: pickedClient?.roles,
              }
            : undefined
        }
        lockedFields={['counterparty']}
        displayFor="financing"
      />
      <ClientLegalDrawer
        isOpen={isClientLegalDrawerOpen}
        onClose={() => setIsClientLegalDrawerOpen(false)}
        client={pickedClient}
        displayFor="financing"
      />
    </fieldset>
  );
}
